export const DESIGN_SYSTEM_GENERATION_RETRIEVAL_REQUEST_SCHEMA_VERSION =
  "cogo.design-system-generation-retrieval-request.v1" as const;
export const DESIGN_SYSTEM_GENERATION_RETRIEVAL_SCHEMA_VERSION =
  "cogo.design-system-generation-retrieval.v1" as const;
export const DESIGN_SYSTEM_GENERATION_RETRIEVAL_ADMISSION_SCHEMA_VERSION =
  "cogo.design-system-generation-retrieval-admission.v1" as const;

export type DesignSystemGenerationTarget =
  | "web"
  | "flutter"
  | "rn"
  | "docx"
  | "hwpx"
  | "pptx"
  | "xlsx"
  | "pdf";

export interface DesignSystemGenerationSourceAuthority {
  sourceId: string;
  fileSha256: string;
  sourceRevision: number;
}

export interface DesignSystemGenerationRequirement {
  requirementId: string;
  componentType?: string;
  semanticRole?: string;
  capabilityRefs?: string[];
  stateRefs?: string[];
  variantRefs?: string[];
}

export interface DesignSystemGenerationRetrievalRequest {
  schemaVersion: typeof DESIGN_SYSTEM_GENERATION_RETRIEVAL_REQUEST_SCHEMA_VERSION;
  intentId: string;
  sourceAuthority: DesignSystemGenerationSourceAuthority;
  target: DesignSystemGenerationTarget;
  requirements: DesignSystemGenerationRequirement[];
  selectedRelease?: {
    packageId?: string;
    releaseId?: string;
  };
  allowCandidate: boolean;
}

export interface DesignSystemGenerationReleaseIdentity {
  packageId: string;
  packageVersion: string;
  releaseId: string;
  packageSnapshotHash: string;
  identityHash: string;
  immutable: boolean;
}

export interface DesignSystemGenerationCatalogComponent {
  componentId: string;
  semanticRole: string;
  description: string;
  targets: DesignSystemGenerationTarget[];
  properties: unknown[];
  slots: Array<{
    slotId: string;
    name: string;
    semanticRole: string;
    cardinality: string;
    acceptedComponentRefs: string[];
  }>;
  capabilityRefs: string[];
  admittedContract: {
    behavior: {
      variants: string[];
      states: string[];
      capabilityRefs: string[];
      eventRefs: string[];
    };
    accessibility: {
      role: string;
      labelRequirement: string;
      keyboardRequirements: string[];
      stateSemantics: string[];
    };
    tokenRefs: string[];
    evidenceRefs: string[];
  };
  sourceRefs: string[];
}

export interface DesignSystemGenerationModelCatalog {
  schemaVersion: "cogo.model-component-catalog.v1";
  packageId: string;
  packageVersion: string;
  releaseIdentity: DesignSystemGenerationReleaseIdentity;
  compilerVersion: string;
  contentHash: string;
  tokens: unknown[];
  components: DesignSystemGenerationCatalogComponent[];
  promptText: string;
  finalJsonWriter: false;
}

export interface DesignSystemGenerationRetrievalMatch {
  requirementId: string;
  decision: "reuse" | "create_candidate";
  matchedComponentId?: string;
  score: number;
  reasons: string[];
  evidenceRefs: string[];
  generationAllowed: boolean;
}

export interface DesignSystemGenerationRetrievalResponse {
  schemaVersion: typeof DESIGN_SYSTEM_GENERATION_RETRIEVAL_SCHEMA_VERSION;
  intentId: string;
  sourceAuthority: DesignSystemGenerationSourceAuthority;
  target: DesignSystemGenerationTarget;
  releaseIdentity: DesignSystemGenerationReleaseIdentity;
  catalog: DesignSystemGenerationModelCatalog;
  matches: DesignSystemGenerationRetrievalMatch[];
  unresolvedRequirements: Array<{
    requirementId: string;
    reasons: string[];
  }>;
  trace: {
    mode: "release-catalog";
    allowCandidate: boolean;
    releaseId: string;
    packageSnapshotHash: string;
    catalogContentHash: string;
    candidateCount: number;
    admittedCount: number;
    embeddingUsed: false;
    fallbackUsed: false;
  };
  generationAllowed: boolean;
  blockedReasons: string[];
  finalJsonWriter: false;
}

export interface OpenPencilRetrievalSourceBundle {
  status: "ready" | "blocked";
  sourceId: string;
  fileSha256?: string;
  revisionLineage?: {
    sourceRevision: number;
  };
}

export interface BuildOpenPencilDesignSystemRetrievalRequestInput {
  intentId: string;
  bundle: OpenPencilRetrievalSourceBundle;
  target: DesignSystemGenerationTarget;
  requirements: DesignSystemGenerationRequirement[];
  selectedRelease?: DesignSystemGenerationRetrievalRequest["selectedRelease"];
}

export interface DesignSystemGenerationRetrievalAdmission {
  schemaVersion: typeof DESIGN_SYSTEM_GENERATION_RETRIEVAL_ADMISSION_SCHEMA_VERSION;
  status: "admitted" | "blocked";
  generationAllowed: boolean;
  blockerIds: string[];
  sourceAuthority: DesignSystemGenerationSourceAuthority;
  releaseRef: {
    packageId: string | null;
    packageVersion: string | null;
    releaseId: string | null;
    packageSnapshotHash: string | null;
    catalogContentHash: string | null;
  };
  matchedRequirementIds: string[];
  finalJsonWriter: false;
}

export class DesignSystemGenerationRetrievalError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "DesignSystemGenerationRetrievalError";
  }
}

const SHA256_PATTERN = /^[a-f0-9]{64}$/;
const CONTENT_HASH_PATTERN = /^sha256:[a-f0-9]{64}$/;

export function buildOpenPencilDesignSystemGenerationRetrievalRequest(
  input: BuildOpenPencilDesignSystemRetrievalRequestInput,
): DesignSystemGenerationRetrievalRequest {
  if (input.bundle.status !== "ready") {
    throw new DesignSystemGenerationRetrievalError(
      "openpencil-source-not-ready",
      "Only a ready OpenPencil source bundle can request design-system generation retrieval.",
    );
  }
  if (!hasText(input.bundle.sourceId)) {
    throw new DesignSystemGenerationRetrievalError(
      "openpencil-source-id-missing",
      "OpenPencil sourceId is required.",
    );
  }
  if (!SHA256_PATTERN.test(input.bundle.fileSha256 ?? "")) {
    throw new DesignSystemGenerationRetrievalError(
      "openpencil-source-hash-invalid",
      "OpenPencil fileSha256 must be a lowercase SHA-256 digest.",
    );
  }
  if (!hasText(input.intentId)) {
    throw new DesignSystemGenerationRetrievalError(
      "retrieval-intent-id-missing",
      "intentId is required.",
    );
  }
  validateRequirements(input.requirements);
  const sourceRevision = input.bundle.revisionLineage?.sourceRevision ?? 1;
  if (!Number.isInteger(sourceRevision) || sourceRevision < 1) {
    throw new DesignSystemGenerationRetrievalError(
      "openpencil-source-revision-invalid",
      "OpenPencil sourceRevision must be a positive integer.",
    );
  }

  return {
    schemaVersion: DESIGN_SYSTEM_GENERATION_RETRIEVAL_REQUEST_SCHEMA_VERSION,
    intentId: input.intentId,
    sourceAuthority: {
      sourceId: input.bundle.sourceId,
      fileSha256: input.bundle.fileSha256!,
      sourceRevision,
    },
    target: input.target,
    requirements: structuredClone(input.requirements),
    ...(input.selectedRelease
      ? { selectedRelease: { ...input.selectedRelease } }
      : {}),
    allowCandidate: false,
  };
}

export function assessDesignSystemGenerationRetrieval(input: {
  request: DesignSystemGenerationRetrievalRequest;
  response: unknown;
  expectedCatalogContentHash?: string;
}): DesignSystemGenerationRetrievalAdmission {
  const blockerIds = validateRequest(input.request);
  const response = asRecord(input.response);
  if (!response) {
    blockerIds.push("retrieval-response-invalid");
    return blockedAdmission(input.request.sourceAuthority, blockerIds);
  }

  if (
    response.schemaVersion !== DESIGN_SYSTEM_GENERATION_RETRIEVAL_SCHEMA_VERSION
  ) {
    blockerIds.push("retrieval-schema-version-mismatch");
  }
  if (response.intentId !== input.request.intentId) {
    blockerIds.push("retrieval-intent-mismatch");
  }
  if (response.target !== input.request.target) {
    blockerIds.push("retrieval-target-mismatch");
  }
  if (input.request.target !== "web") {
    blockerIds.push("retrieval-target-not-verified");
  }
  if (
    !sameSourceAuthority(
      response.sourceAuthority,
      input.request.sourceAuthority,
    )
  ) {
    blockerIds.push("retrieval-source-authority-mismatch");
  }
  if (response.finalJsonWriter !== false) {
    blockerIds.push("retrieval-final-json-writer-violation");
  }

  const releaseIdentity = readReleaseIdentity(response.releaseIdentity);
  const catalog = readCatalog(response.catalog);
  const trace = asRecord(response.trace);

  if (!releaseIdentity) {
    blockerIds.push("retrieval-release-identity-invalid");
  }
  if (!catalog) {
    blockerIds.push("retrieval-catalog-invalid");
  }
  if (!trace) {
    blockerIds.push("retrieval-trace-invalid");
  }

  if (releaseIdentity && catalog) {
    if (!sameReleaseIdentity(releaseIdentity, catalog.releaseIdentity)) {
      blockerIds.push("retrieval-catalog-release-mismatch");
    }
    if (
      catalog.packageId !== releaseIdentity.packageId ||
      catalog.packageVersion !== releaseIdentity.packageVersion
    ) {
      blockerIds.push("retrieval-catalog-package-mismatch");
    }
    if (!releaseIdentity.immutable) {
      blockerIds.push("retrieval-release-mutable");
    }
    if (!CONTENT_HASH_PATTERN.test(releaseIdentity.packageSnapshotHash)) {
      blockerIds.push("retrieval-package-hash-invalid");
    }
    if (!CONTENT_HASH_PATTERN.test(releaseIdentity.identityHash)) {
      blockerIds.push("retrieval-identity-hash-invalid");
    }
    if (!CONTENT_HASH_PATTERN.test(catalog.contentHash)) {
      blockerIds.push("retrieval-catalog-hash-invalid");
    }
    if (catalog.finalJsonWriter !== false) {
      blockerIds.push("retrieval-catalog-final-json-writer-violation");
    }
    if (catalog.components.length === 0 || catalog.tokens.length === 0) {
      blockerIds.push("retrieval-catalog-empty");
    }
  }

  if (releaseIdentity && input.request.selectedRelease?.packageId) {
    if (releaseIdentity.packageId !== input.request.selectedRelease.packageId) {
      blockerIds.push("retrieval-selected-package-mismatch");
    }
  }
  if (releaseIdentity && input.request.selectedRelease?.releaseId) {
    if (releaseIdentity.releaseId !== input.request.selectedRelease.releaseId) {
      blockerIds.push("retrieval-selected-release-mismatch");
    }
  }

  if (trace) {
    if (trace.mode !== "release-catalog") {
      blockerIds.push("retrieval-trace-not-release-catalog");
    }
    if (trace.allowCandidate !== false || input.request.allowCandidate) {
      blockerIds.push("retrieval-candidate-authority-enabled");
    }
    if (trace.embeddingUsed !== false || trace.fallbackUsed !== false) {
      blockerIds.push("retrieval-nondeterministic-fallback-used");
    }
    if (
      !Number.isInteger(trace.candidateCount) ||
      trace.candidateCount < 0 ||
      !Number.isInteger(trace.admittedCount) ||
      trace.admittedCount < 0
    ) {
      blockerIds.push("retrieval-trace-count-invalid");
    }
    if (
      releaseIdentity &&
      (trace.releaseId !== releaseIdentity.releaseId ||
        trace.packageSnapshotHash !== releaseIdentity.packageSnapshotHash)
    ) {
      blockerIds.push("retrieval-trace-release-mismatch");
    }
    if (catalog && trace.catalogContentHash !== catalog.contentHash) {
      blockerIds.push("retrieval-trace-catalog-mismatch");
    }
  }

  if (
    input.expectedCatalogContentHash &&
    catalog?.contentHash !== input.expectedCatalogContentHash
  ) {
    blockerIds.push("retrieval-expected-catalog-mismatch");
  }

  const matches = readMatches(response.matches);
  const unresolved = Array.isArray(response.unresolvedRequirements)
    ? response.unresolvedRequirements
    : null;
  if (!matches) blockerIds.push("retrieval-matches-invalid");
  if (!unresolved) blockerIds.push("retrieval-unresolved-invalid");
  if (unresolved && unresolved.length > 0) {
    blockerIds.push("retrieval-requirements-unresolved");
  }

  const matchedRequirementIds: string[] = [];
  if (matches && catalog) {
    const expectedIds = new Set(
      input.request.requirements.map((item) => item.requirementId),
    );
    const seen = new Set<string>();
    for (const match of matches) {
      if (!expectedIds.has(match.requirementId)) {
        blockerIds.push("retrieval-unexpected-requirement-match");
      }
      if (seen.has(match.requirementId)) {
        blockerIds.push("retrieval-duplicate-requirement-match");
      }
      seen.add(match.requirementId);
      if (
        match.decision !== "reuse" ||
        match.generationAllowed !== true ||
        !hasText(match.matchedComponentId)
      ) {
        blockerIds.push("retrieval-match-not-admitted");
        continue;
      }
      const component = catalog.components.find(
        (item) => item.componentId === match.matchedComponentId,
      );
      if (!component || !component.targets.includes(input.request.target)) {
        blockerIds.push("retrieval-matched-component-not-in-catalog");
        continue;
      }
      matchedRequirementIds.push(match.requirementId);
    }
    for (const requirementId of expectedIds) {
      if (!seen.has(requirementId)) {
        blockerIds.push("retrieval-required-match-missing");
      }
    }
  }

  if (response.generationAllowed !== true) {
    blockerIds.push("retrieval-generation-not-allowed");
  }
  if (!isStringArray(response.blockedReasons)) {
    blockerIds.push("retrieval-blocked-reasons-invalid");
  } else if (response.blockedReasons.length > 0) {
    blockerIds.push("retrieval-server-blocked");
  }

  const uniqueBlockers = [...new Set(blockerIds)].sort();
  const generationAllowed = uniqueBlockers.length === 0;
  return {
    schemaVersion: DESIGN_SYSTEM_GENERATION_RETRIEVAL_ADMISSION_SCHEMA_VERSION,
    status: generationAllowed ? "admitted" : "blocked",
    generationAllowed,
    blockerIds: uniqueBlockers,
    sourceAuthority: input.request.sourceAuthority,
    releaseRef: {
      packageId: releaseIdentity?.packageId ?? null,
      packageVersion: releaseIdentity?.packageVersion ?? null,
      releaseId: releaseIdentity?.releaseId ?? null,
      packageSnapshotHash: releaseIdentity?.packageSnapshotHash ?? null,
      catalogContentHash: catalog?.contentHash ?? null,
    },
    matchedRequirementIds: [...new Set(matchedRequirementIds)].sort(),
    finalJsonWriter: false,
  };
}

export function assertDesignSystemGenerationRetrievalAdmitted(input: {
  request: DesignSystemGenerationRetrievalRequest;
  response: unknown;
  expectedCatalogContentHash?: string;
}): DesignSystemGenerationRetrievalAdmission {
  const admission = assessDesignSystemGenerationRetrieval(input);
  if (!admission.generationAllowed) {
    throw new DesignSystemGenerationRetrievalError(
      "design-system-generation-retrieval-blocked",
      `Design-system generation retrieval blocked: ${admission.blockerIds.join(", ")}`,
    );
  }
  return admission;
}

function validateRequirements(
  requirements: DesignSystemGenerationRequirement[],
): void {
  if (!Array.isArray(requirements) || requirements.length === 0) {
    throw new DesignSystemGenerationRetrievalError(
      "retrieval-requirements-missing",
      "At least one design-system generation requirement is required.",
    );
  }
  const ids = new Set<string>();
  for (const requirement of requirements) {
    if (!hasText(requirement.requirementId)) {
      throw new DesignSystemGenerationRetrievalError(
        "retrieval-requirement-id-missing",
        "Every design-system generation requirement needs a requirementId.",
      );
    }
    if (ids.has(requirement.requirementId)) {
      throw new DesignSystemGenerationRetrievalError(
        "retrieval-requirement-id-duplicate",
        `Duplicate requirementId: ${requirement.requirementId}`,
      );
    }
    ids.add(requirement.requirementId);
    if (
      !hasText(requirement.componentType) &&
      !hasText(requirement.semanticRole) &&
      (requirement.capabilityRefs?.length ?? 0) === 0
    ) {
      throw new DesignSystemGenerationRetrievalError(
        "retrieval-requirement-unmatchable",
        `Requirement ${requirement.requirementId} has no component type, semantic role, or capability.`,
      );
    }
  }
}

function validateRequest(
  request: DesignSystemGenerationRetrievalRequest,
): string[] {
  const blockers: string[] = [];
  if (
    request.schemaVersion !==
    DESIGN_SYSTEM_GENERATION_RETRIEVAL_REQUEST_SCHEMA_VERSION
  ) {
    blockers.push("retrieval-request-schema-version-mismatch");
  }
  if (!hasText(request.intentId)) blockers.push("retrieval-intent-id-missing");
  if (!hasText(request.sourceAuthority?.sourceId)) {
    blockers.push("retrieval-source-id-missing");
  }
  if (!SHA256_PATTERN.test(request.sourceAuthority?.fileSha256 ?? "")) {
    blockers.push("retrieval-source-hash-invalid");
  }
  if (
    !Number.isInteger(request.sourceAuthority?.sourceRevision) ||
    request.sourceAuthority.sourceRevision < 1
  ) {
    blockers.push("retrieval-source-revision-invalid");
  }
  try {
    validateRequirements(request.requirements);
  } catch (error) {
    blockers.push(
      error instanceof DesignSystemGenerationRetrievalError
        ? error.code
        : "retrieval-requirements-invalid",
    );
  }
  return blockers;
}

function readReleaseIdentity(
  value: unknown,
): DesignSystemGenerationReleaseIdentity | null {
  const record = asRecord(value);
  if (
    !record ||
    !hasText(record.packageId) ||
    !hasText(record.packageVersion) ||
    !hasText(record.releaseId) ||
    !hasText(record.packageSnapshotHash) ||
    !hasText(record.identityHash) ||
    typeof record.immutable !== "boolean"
  ) {
    return null;
  }
  return record as unknown as DesignSystemGenerationReleaseIdentity;
}

function readCatalog(
  value: unknown,
): DesignSystemGenerationModelCatalog | null {
  const record = asRecord(value);
  const releaseIdentity = readReleaseIdentity(record?.releaseIdentity);
  if (
    !record ||
    record.schemaVersion !== "cogo.model-component-catalog.v1" ||
    !hasText(record.packageId) ||
    !hasText(record.packageVersion) ||
    !hasText(record.compilerVersion) ||
    !hasText(record.contentHash) ||
    !releaseIdentity ||
    !Array.isArray(record.tokens) ||
    !Array.isArray(record.components) ||
    typeof record.promptText !== "string" ||
    record.finalJsonWriter !== false
  ) {
    return null;
  }
  const components = record.components.filter(isCatalogComponent);
  if (components.length !== record.components.length) return null;
  return {
    ...(record as unknown as DesignSystemGenerationModelCatalog),
    releaseIdentity,
    components,
  };
}

function isCatalogComponent(
  value: unknown,
): value is DesignSystemGenerationCatalogComponent {
  const record = asRecord(value);
  const contract = asRecord(record?.admittedContract);
  const behavior = asRecord(contract?.behavior);
  const accessibility = asRecord(contract?.accessibility);
  return Boolean(
    record &&
    hasText(record.componentId) &&
    hasText(record.semanticRole) &&
    Array.isArray(record.targets) &&
    Array.isArray(record.properties) &&
    Array.isArray(record.slots) &&
    Array.isArray(record.capabilityRefs) &&
    contract &&
    behavior &&
    Array.isArray(behavior.variants) &&
    Array.isArray(behavior.states) &&
    Array.isArray(behavior.capabilityRefs) &&
    Array.isArray(behavior.eventRefs) &&
    accessibility &&
    hasText(accessibility.role) &&
    Array.isArray(contract.tokenRefs) &&
    Array.isArray(contract.evidenceRefs) &&
    Array.isArray(record.sourceRefs),
  );
}

function readMatches(
  value: unknown,
): DesignSystemGenerationRetrievalMatch[] | null {
  if (!Array.isArray(value)) return null;
  const matches: DesignSystemGenerationRetrievalMatch[] = [];
  for (const item of value) {
    const record = asRecord(item);
    if (
      !record ||
      !hasText(record.requirementId) ||
      (record.decision !== "reuse" && record.decision !== "create_candidate") ||
      typeof record.score !== "number" ||
      record.score < 0 ||
      record.score > 1 ||
      !isStringArray(record.reasons) ||
      !isStringArray(record.evidenceRefs) ||
      typeof record.generationAllowed !== "boolean"
    ) {
      return null;
    }
    matches.push(record as unknown as DesignSystemGenerationRetrievalMatch);
  }
  return matches;
}

function sameSourceAuthority(
  value: unknown,
  expected: DesignSystemGenerationSourceAuthority,
): boolean {
  const record = asRecord(value);
  return Boolean(
    record &&
    record.sourceId === expected.sourceId &&
    record.fileSha256 === expected.fileSha256 &&
    record.sourceRevision === expected.sourceRevision,
  );
}

function sameReleaseIdentity(
  left: DesignSystemGenerationReleaseIdentity,
  right: DesignSystemGenerationReleaseIdentity,
): boolean {
  return (
    left.packageId === right.packageId &&
    left.packageVersion === right.packageVersion &&
    left.releaseId === right.releaseId &&
    left.packageSnapshotHash === right.packageSnapshotHash &&
    left.identityHash === right.identityHash &&
    left.immutable === right.immutable
  );
}

function blockedAdmission(
  sourceAuthority: DesignSystemGenerationSourceAuthority,
  blockerIds: string[],
): DesignSystemGenerationRetrievalAdmission {
  return {
    schemaVersion: DESIGN_SYSTEM_GENERATION_RETRIEVAL_ADMISSION_SCHEMA_VERSION,
    status: "blocked",
    generationAllowed: false,
    blockerIds: [...new Set(blockerIds)].sort(),
    sourceAuthority,
    releaseRef: {
      packageId: null,
      packageVersion: null,
      releaseId: null,
      packageSnapshotHash: null,
      catalogContentHash: null,
    },
    matchedRequirementIds: [],
    finalJsonWriter: false,
  };
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every(hasText);
}

function asRecord(value: unknown): Record<string, any> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, any>)
    : null;
}

function hasText(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}
